/**
 * R10 Flag: capture this moment of a running Penelope game on the relay (`POST /api/penelope/games/:id/flag`) and copy
 * the evidence line in Doom's Baseline format — "LAL @ GSW / 2026-10-20 | 54–61 / Q3 4:12 | 19:42:05 PT | <saw>" — so a
 * report names a PT time, the score and the clock instead of an audio timestamp. The relay keeps the full moment
 * (snapshot, books, fair, recent decisions) in the game's flags log.
 */
import { useCallback, useState } from 'react';
import { apiFetch } from '@/stores/authStore';

export interface PenelopeFlagState {
  busy: boolean;
  /** The last evidence line, as copied. */
  line: string | null;
  /** Whether the clipboard took it (a browser may refuse outside a user gesture). */
  copied: boolean;
  error: string | null;
}

/** Copy `text`, quietly: false when the browser will not. */
async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard?.writeText(text);
    return Boolean(navigator.clipboard);
  } catch {
    return false;
  }
}

export function usePenelopeFlag(gameId: string | null | undefined): PenelopeFlagState & {
  flag: (opts: { note?: string; status?: string; source: string }) => Promise<string | null>;
} {
  const [s, setS] = useState<PenelopeFlagState>({ busy: false, line: null, copied: false, error: null });
  const flag = useCallback(async (opts: { note?: string; status?: string; source: string }): Promise<string | null> => {
    if (!gameId) return null;
    setS((p) => ({ ...p, busy: true, error: null }));
    try {
      const res = await apiFetch(`/api/penelope/games/${gameId}/flag`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(opts),
      });
      const body = (await res.json().catch(() => ({}))) as { line?: string; error?: string };
      if (!res.ok || !body.line) {
        setS({ busy: false, line: null, copied: false, error: body.error ?? `HTTP ${res.status}` });
        return null;
      }
      const copied = await copy(body.line);
      setS({ busy: false, line: body.line, copied, error: null });
      return body.line;
    } catch (err) {
      setS({ busy: false, line: null, copied: false, error: (err as Error).message });
      return null;
    }
  }, [gameId]);
  return { ...s, flag };
}
